import React, { useCallback, useEffect, useMemo, useState } from "react";
import BreadCrumb from "Common/BreadCrumb";
import {
  Plus,
  Trash2,
  Pencil,
  Download,
  CheckCircle,
  XCircle,
  RefreshCw,
} from "lucide-react";
import TableContainer from "Common/TableContainer";
import DeleteModal from "Common/DeleteModal";
import Modal from "Common/Components/Modal";
import * as Yup from "yup";
import { useFormik } from "formik";
import { toast, ToastContainer } from "react-toastify";
import TableDropDownSmall from "pages/Components/TableDropDownSmall";
import { exportToExcel } from "Common/Components/Excel";
import PaginationTest from "Common/Components/Paginationtest";
import LoadingCounter from "Common/Components/LoadingCounterProps";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { getRoleId } from "Common/Components/getRoleId";
import { useAssistantFilter } from "Common/Assistant/lib/pageFilter";

// ========== تبدیل تاریخ به شمسی ==========
const toPersianDate = (isoDateString: string): string => {
  if (!isoDateString) return "";
  const date = new Date(isoDateString);
  if (isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("fa-IR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
};

// ========== تایپ‌ها ==========
interface UserItem {
  created_at: string;
  created_at_jalali: string;
  updated_at: string;
  updated_at_jalali: string;
  id: number;
  username: string;
  email: string;
  fullname: string;
  phone_number: string;
  role_id: number;
  role: string;
  city_id: number;
  province_id: number;
  disabled: boolean;
  shipping_company: string | null;
}

interface UsersApiResponse {
  total: number;
  size: number;
  pages: number;
  items: UserItem[];
}

// ========== نقش‌های قابل انتخاب ==========
const roleOptions = [
  { label: "ادمین", value: 1 },
  { label: "بازرس شهرستان", value: 2 },
  { label: "ناظر هوش مصنوعی", value: 3 },
  { label: "راننده", value: 4 },
  { label: "مدیر استان", value: 5 },
  { label: "مدیر شهرستان", value: 6 },
  { label: "شرکت حمل", value: 7 },
];

const ManageUser = () => {
  const navigate = useNavigate();
  const userRole = getRoleId(localStorage.getItem("app_havir")) || 50;

  // ========== state‌های لیست ==========
  const [users, setUsers] = useState<UserItem[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedNumber, setSelectedNumber] = useState<number>(10);

  // فیلتری که دستیار سامانه فرستاده (از گفتگو یا با صدا) را برمی‌دارد
  useAssistantFilter("users", (filter) => {
    if (filter.pageSize) setSelectedNumber(filter.pageSize);
    setSearchTerm(filter.search || "");
    setCurrentPage(1);
  });

  // ========== لیست‌های دراپ‌داون ==========
  const [provincesList, setProvincesList] = useState<{ province_id: number; province: string }[]>([]);
  const [citiesList, setCitiesList] = useState<{ city_id: number; city: string }[]>([]);
  const [shippingCompanies, setShippingCompanies] = useState<string[]>([]);

  // ========== کنترل نمایش دراپ‌دان شرکت حمل ==========
  const [showShippingCompany, setShowShippingCompany] = useState(false);

  // ========== مودال‌ها ==========
  const [showModal, setShowModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  // ========== دریافت لیست‌های کمکی ==========
  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        const [provRes, cityRes, shipRes] = await Promise.all([
          axios.get("https://rasad-api.havirard.ir/dropdown/provinces", {
            headers: { Authorization: `Bearer ${localStorage.getItem("app_havir")}` },
          }),
          axios.get("https://rasad-api.havirard.ir/dropdown/cities", {
            headers: { Authorization: `Bearer ${localStorage.getItem("app_havir")}` },
          }),
          axios.get("https://rasad-api.havirard.ir/dropdown/shipping-companies", {
            headers: { Authorization: `Bearer ${localStorage.getItem("app_havir")}` },
          }),
        ]);
        setProvincesList(provRes.data || []);
        setCitiesList(cityRes.data || []);
        // استخراج نام شرکت‌ها از پاسخ
        const companies = (shipRes.data || []).map((item: any) => item.shipping_company);
        setShippingCompanies(companies);
      } catch (err) {
        console.error("Dropdown fetch error:", err);
      }
    };
    fetchDropdowns();
  }, []);

  // ========== دریافت لیست کاربران ==========
  const fetchUsers = useCallback(async (page: number, size: number) => {
    setLoading(true);
    setErrorMessage("");
    try {
      const params: any = { page, size, sort_by: "id", sort_order: "desc" };
      if (searchTerm?.trim()) params.search = searchTerm.trim();
      const res = await axios.get<UsersApiResponse>("https://rasad-api.havirard.ir/users/", {
        params,
        headers: { Authorization: `Bearer ${localStorage.getItem("app_havir")}` },
      });
      setUsers(res.data.items ?? []);
      setTotal(res.data.total ?? 0);
      setTotalPages(res.data.pages ?? 0);
    } catch (err: any) {
      if (err.response?.status === 401) {
        localStorage.removeItem("app_havir");
        localStorage.removeItem("app_rt");
        navigate("/login");
      } else if (err.response) {
        setErrorMessage(`خطای ${err.response.status}: ${err.response.data?.message || "خطا در دریافت داده‌ها"}`);
      } else {
        setErrorMessage("ارتباط با سرور برقرار نشد");
      }
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, navigate]);

  useEffect(() => {
    fetchUsers(currentPage, selectedNumber);
  }, [currentPage, selectedNumber, fetchUsers]);

  // ========== هندلرهای صفحه‌بندی و جستجو ==========
  const handlePageChange = (page: number) => setCurrentPage(page);
  const handleSizeChange = (value: number) => {
    setSelectedNumber(value);
    setCurrentPage(1);
  };
  const handleSearchChange = (val: string) => {
    setSearchTerm(val);
    setCurrentPage(1);
  };

  // ========== عملیات حذف ==========
  const handleDeleteClick = (id: number) => {
    setDeleteId(id);
    setDeleteModal(true);
  };
  const confirmDelete = async () => {
    if (deleteId === null) return;
    try {
      await axios.delete(`https://rasad-api.havirard.ir/users/${deleteId}/`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("app_havir")}` },
      });
      toast.success("کاربر با موفقیت حذف شد");
      setDeleteModal(false);
      setDeleteId(null);
      fetchUsers(currentPage, selectedNumber);
    } catch (err: any) {
      toast.error(err.response?.data?.message || "خطا در حذف کاربر");
    }
  };

  // ========== مودال افزودن/ویرایش ==========
  const openAddModal = () => {
    setIsEdit(false);
    setEditId(null);
    validation.resetForm();
    setShowShippingCompany(false);
    setShowModal(true);
  };

  const openEditModal = (user: UserItem) => {
    setIsEdit(true);
    setEditId(user.id);
    validation.setValues({
      username: user.username,
      fullname: user.fullname,
      phone_number: user.phone_number,
      email: user.email,
      password: "",
      role_id: user.role_id,
      city_id: user.city_id || 0,
      province_id: user.province_id || 0,
      disabled: user.disabled,
      shipping_company: user.shipping_company || "",
    });
    setShowShippingCompany(user.role_id === 7);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    validation.resetForm();
    setShowShippingCompany(false);
  };

  // ========== هندلر تغییر نقش ==========
  const handleRoleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const roleId = Number(e.target.value);
    validation.handleChange(e);
    setShowShippingCompany(roleId === 7);
  };

  // ========== اعتبارسنجی فرم ==========
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      username: "",
      fullname: "",
      phone_number: "",
      email: "",
      password: "",
      role_id: 1,
      city_id: 0,
      province_id: 0,
      disabled: false,
      shipping_company: "",
    },
    validationSchema: Yup.object({
      username: Yup.string().required("نام کاربری الزامی است"),
      fullname: Yup.string().required("نام کامل الزامی است"),
      phone_number: Yup.string()
        .matches(/^\d{11}$/, "شماره تلفن باید ۱۱ رقم باشد")
        .required("شماره تلفن الزامی است"),
      email: Yup.string().email("ایمیل نامعتبر").required("ایمیل الزامی است"),
      password: Yup.string().when([], {
        is: () => !isEdit,
        then: (schema) => schema.required("رمز عبور برای کاربر جدید الزامی است").min(8, "حداقل ۸ کاراکتر"),
        otherwise: (schema) => schema.notRequired(),
      }),
      shipping_company: Yup.string()
        .when("role_id", {
          is: (roleId: number) => roleId === 7,
          then: (schema) => schema.required("انتخاب شرکت حمل الزامی است"),
          otherwise: (schema) => schema.notRequired(),
        }),
    }),
    onSubmit: async (values) => {
      try {
        // تبدیل فیلدهای عددی
        const payload: any = {
          ...values,
          city_id: Number(values.city_id),
          province_id: Number(values.province_id),
          role_id: Number(values.role_id),
        };

        // اگر نقش شرکت حمل است، shipping_company را نگه دار
        if (payload.role_id === 7) {
          payload.shipping_company = values.shipping_company;
        } else {
          delete payload.shipping_company;
        }

        // اگر رمز عبور خالی است (در ویرایش)، حذفش کن
        if (isEdit && !payload.password) {
          delete payload.password;
        }

        if (isEdit && editId !== null) {
          await axios.patch(`https://rasad-api.havirard.ir/users/${editId}/`, payload, {
            headers: { Authorization: `Bearer ${localStorage.getItem("app_havir")}` },
          });
          toast.success("کاربر با موفقیت ویرایش شد");
        } else {
          await axios.post("https://rasad-api.havirard.ir/users/", payload, {
            headers: { Authorization: `Bearer ${localStorage.getItem("app_havir")}` },
          });
          toast.success("کاربر جدید با موفقیت ایجاد شد");
        }
        closeModal();
        fetchUsers(currentPage, selectedNumber);
      } catch (err: any) {
        console.error(err);
        toast.error(err.response?.data?.message || "خطا در ذخیره کاربر");
      }
    },
  });

  // ========== ستون‌های جدول ==========
  const columns = useMemo(() => {
    const formatValue = (value: any) => {
      if (value === null || value === undefined || value === "" || value === "null") return "-";
      return value;
    };

    const baseColumns: any[] = [
      { header: "شناسه", accessorKey: "id", enableColumnFilter: false, cell: (cell: any) => formatValue(cell.getValue()) },
      { header: "نام کاربری", accessorKey: "username", enableColumnFilter: false, cell: (cell: any) => formatValue(cell.getValue()) },
      { header: "نام کامل", accessorKey: "fullname", enableColumnFilter: false, cell: (cell: any) => formatValue(cell.getValue()) },
      { header: "شماره تلفن", accessorKey: "phone_number", enableColumnFilter: false, cell: (cell: any) => formatValue(cell.getValue()) },
      { header: "ایمیل", accessorKey: "email", enableColumnFilter: false, cell: (cell: any) => formatValue(cell.getValue()) },
      {
        header: "نقش",
        accessorKey: "role_id",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const role = roleOptions.find((r) => r.value === cell.row.original.role_id);
          return role?.label || cell.row.original.role || "نامشخص";
        },
      },
      {
        header: "وضعیت",
        accessorKey: "disabled",
        enableColumnFilter: false,
        cell: (cell: any) =>
          cell.getValue() ? (
            <span className="flex items-center gap-1 text-red-500"><XCircle size={16} /> غیرفعال</span>
          ) : (
            <span className="flex items-center gap-1 text-green-500"><CheckCircle size={16} /> فعال</span>
          ),
      },
      {
        header: "تاریخ ایجاد",
        accessorKey: "created_at_jalali",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const value = cell.getValue();
          if (value) return value;
          const created = cell.row.original.created_at;
          return created ? toPersianDate(created) : "-";
        },
      },
    ];

    // فقط نقش‌های غیر از ۵، ۶ و ۷ بتوانند ویرایش و حذف کنند
    if (userRole !== 5 && userRole !== 6 && userRole !== 7) {
      baseColumns.push({
        header: "عملیات",
        enableColumnFilter: false,
        cell: (cell: any) => (
          <div className="flex gap-2">
            <button
              onClick={() => openEditModal(cell.row.original)}
              className="p-2 text-blue-500 rounded-lg hover:bg-blue-50"
              title="ویرایش"
            >
              <Pencil size={18} />
            </button>
            <button
              onClick={() => handleDeleteClick(cell.row.original.id)}
              className="p-2 text-red-500 rounded-lg hover:bg-red-50"
              title="حذف"
            >
              <Trash2 size={18} />
            </button>
          </div>
        ),
      });
    }

    return baseColumns;
  }, [userRole, openEditModal, handleDeleteClick]);

  const handleExportExcel = () => {
    const exportData = users.map((u) => ({
      "شناسه": u.id,
      "نام کاربری": u.username,
      "نام کامل": u.fullname,
      "تلفن": u.phone_number,
      "ایمیل": u.email,
      "نقش": roleOptions.find((r) => r.value === u.role_id)?.label || u.role,
      "وضعیت": u.disabled ? "غیرفعال" : "فعال",
      "تاریخ ایجاد": u.created_at_jalali || toPersianDate(u.created_at),
    }));
    exportToExcel(exportData, "UsersList");
  };

  return (
    <React.Fragment>
      <BreadCrumb pageTitle="مدیریت کاربران" title="پایش آرد" />
      <DeleteModal show={deleteModal} onHide={() => setDeleteModal(false)} onDelete={confirmDelete} />
      <ToastContainer position="top-center" closeButton={false} limit={3} rtl={true} />

      <div className="card">
        <div className="card-body">
          <div className="flex flex-col gap-4 mb-4 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-2">
              <RefreshCw
                className={`size-5 text-blue-500 cursor-pointer ${loading ? "animate-spin" : ""}`}
                onClick={() => fetchUsers(currentPage, selectedNumber)}
              />
              <h6 className="text-lg font-semibold">
                مجموع کاربران{" "}
                <span className="px-2 py-0.5 text-sm bg-blue-100 text-blue-700 rounded-full">
                  {total}
                </span>
              </h6>
            </div>

            <div className="flex flex-wrap gap-3 items-center">
              <TableDropDownSmall onSelect={handleSizeChange} />
              <button onClick={handleExportExcel} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 bg-white border border-blue-200 rounded-xl hover:bg-blue-50">
                <Download size={16} /> اکسل
              </button>
              {userRole !== 5 && userRole !== 6 && userRole !== 7 && (
                <button onClick={openAddModal} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-xl hover:bg-blue-700">
                  <Plus size={16} /> ایجاد کاربر
                </button>
              )}
            </div>
          </div>

          <div className="relative mb-2">
            <input
              type="text"
              placeholder="جستجو..."
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="w-64 pr-10 pl-4 py-2 text-sm border rounded-xl focus:ring-2 focus:ring-blue-500 dark:bg-gray-800"
            />
          </div>

          {loading ? (
            <div className="py-12 text-center text-gray-500">در حال بارگیری...</div>
          ) : users.length === 0 ? (
            <LoadingCounter count={0} text={errorMessage || "کاربری یافت نشد"} />
          ) : (
            <>
              <TableContainer
                isPagination={false}
                columns={columns}
                data={users}
                isGlobalFilter={false}
                customPageSize={selectedNumber}
                divclassName="-mx-5 overflow-x-auto"
                tableclassName="w-full whitespace-nowrap"
                theadclassName="ltr:text-left rtl:text-right bg-slate-100 dark:bg-zink-600"
                thclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 font-semibold border-b border-slate-200 dark:border-zink-500"
                tdclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 border-y border-slate-200 dark:border-zink-500"
                PaginationClassName="hidden"
              />
              <PaginationTest
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </>
          )}
        </div>
      </div>

      {/* مودال ایجاد/ویرایش کاربر */}
      <Modal
        show={showModal}
        onHide={closeModal}
        modal-center="true"
        className="fixed flex flex-col transition-all duration-300 ease-in-out left-2/4 z-drawer -translate-x-2/4 -translate-y-2/4"
        dialogClassName="w-screen md:w-[42rem] bg-white rounded-2xl shadow-2xl dark:bg-gray-800"
      >
        <Modal.Header className="flex items-center justify-between p-5 border-b dark:border-gray-700">
          <Modal.Title className="text-lg font-bold">
            {isEdit ? "ویرایش کاربر" : "ایجاد کاربر جدید"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-5 max-h-[70vh] overflow-y-auto">
          <form id="user-form" onSubmit={validation.handleSubmit}>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="block mb-1 text-sm font-medium">نام کاربری *</label>
                <input
                  name="username"
                  type="text"
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.username}
                />
                {validation.touched.username && validation.errors.username && (
                  <p className="text-xs text-red-500 mt-1">{validation.errors.username}</p>
                )}
              </div>
              <div>
                <label className="block mb-1 text-sm font-medium">نام کامل *</label>
                <input
                  name="fullname"
                  type="text"
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.fullname}
                />
                {validation.touched.fullname && validation.errors.fullname && (
                  <p className="text-xs text-red-500 mt-1">{validation.errors.fullname}</p>
                )}
              </div>
              <div>
                <label className="block mb-1 text-sm font-medium">شماره تماس (۱۱ رقم) *</label>
                <input
                  name="phone_number"
                  type="text"
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.phone_number}
                />
                {validation.touched.phone_number && validation.errors.phone_number && (
                  <p className="text-xs text-red-500 mt-1">{validation.errors.phone_number}</p>
                )}
              </div>
              <div>
                <label className="block mb-1 text-sm font-medium">ایمیل *</label>
                <input
                  name="email"
                  type="email"
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.email}
                />
                {validation.touched.email && validation.errors.email && (
                  <p className="text-xs text-red-500 mt-1">{validation.errors.email}</p>
                )}
              </div>
              <div>
                <label className="block mb-1 text-sm font-medium">
                  رمز عبور {!isEdit && "*"} {isEdit && "(اختیاری)"}
                </label>
                <input
                  name="password"
                  type="password"
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.password}
                  placeholder={isEdit ? "در صورت عدم تغییر خالی بگذارید" : ""}
                />
                {validation.touched.password && validation.errors.password && (
                  <p className="text-xs text-red-500 mt-1">{validation.errors.password}</p>
                )}
              </div>
              <div>
                <label className="block mb-1 text-sm font-medium">نقش *</label>
                <select
                  name="role_id"
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
                  onChange={handleRoleChange}
                  value={validation.values.role_id}
                >
                  <option value="">انتخاب نقش</option>
                  {roleOptions.map((role) => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* ========== فیلد شرکت حمل (فقط در صورت انتخاب نقش ۷) ========== */}
              {showShippingCompany && (
                <div className="sm:col-span-2">
                  <label className="block mb-1 text-sm font-medium">شرکت حمل *</label>
                  <select
                    name="shipping_company"
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.shipping_company || ""}
                  >
                    <option value="">انتخاب شرکت حمل</option>
                    {shippingCompanies.map((company) => (
                      <option key={company} value={company}>
                        {company}
                      </option>
                    ))}
                  </select>
                  {validation.touched.shipping_company && validation.errors.shipping_company && (
                    <p className="text-xs text-red-500 mt-1">{validation.errors.shipping_company}</p>
                  )}
                </div>
              )}

              {/* ========== استان ========== */}
              <div>
                <label className="block mb-1 text-sm font-medium">استان</label>
                <select
                  name="province_id"
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
                  onChange={validation.handleChange}
                  value={validation.values.province_id}
                >
                  <option value={0}>انتخاب استان</option>
                  {provincesList.map((p) => (
                    <option key={p.province_id} value={p.province_id}>
                      {p.province}
                    </option>
                  ))}
                </select>
              </div>

              {/* ========== شهر ========== */}
              <div>
                <label className="block mb-1 text-sm font-medium">شهر</label>
                <select
                  name="city_id"
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
                  onChange={validation.handleChange}
                  value={validation.values.city_id}
                >
                  <option value={0}>انتخاب شهر</option>
                  {citiesList.map((c) => (
                    <option key={c.city_id} value={c.city_id}>
                      {c.city}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-3 mt-2">
                <span className="text-sm font-medium">وضعیت:</span>
                <button
                  type="button"
                  onClick={() => validation.setFieldValue("disabled", !validation.values.disabled)}
                  className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${validation.values.disabled
                      ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"
                      : "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
                    }`}
                >
                  {validation.values.disabled ? "غیرفعال" : "فعال"}
                </button>
              </div>
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer className="flex justify-end gap-3 p-5 border-t dark:border-gray-700">
          <button
            onClick={closeModal}
            className="px-4 py-2 text-sm bg-gray-100 rounded-lg hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600"
          >
            انصراف
          </button>
          <button
            type="submit"
            form="user-form"
            className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700"
          >
            {isEdit ? "بروزرسانی" : "ثبت کاربر"}
          </button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
};

export default ManageUser;